"use client";

import { useState, useCallback, useMemo, useEffect } from "react";
import Link from "next/link";
import { formatDistanceToNow } from "date-fns";
import { ru } from "date-fns/locale";
import { MessageSquare, MoreHorizontal, Pencil, Trash2, Flag, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth/auth-context";
import type { CommentResponse, SortOption } from "@/lib/types/comments";
import {
  getProductComments,
  createComment,
  updateComment,
  deleteComment,
  voteOnComment,
  reportComment,
} from "@/lib/services/commentsService";

// ============================================================================
// ICONS
// ============================================================================

function UpArrow({ active }: { active?: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M8 3L14 9H10V13H6V9H2L8 3Z"
        fill={active ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DownArrow({ active }: { active?: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M8 13L2 7H6V3H10V7H14L8 13Z"
        fill={active ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

function updateInTree(list: CommentResponse[], id: number, fn: (c: CommentResponse) => CommentResponse): CommentResponse[] {
  return list.map((c) => (c.id === id ? fn(c) : c.children?.length ? { ...c, children: updateInTree(c.children, id, fn) } : c));
}

function removeFromTree(list: CommentResponse[], id: number): CommentResponse[] {
  return list.filter((c) => c.id !== id).map((c) => ({ ...c, children: c.children?.length ? removeFromTree(c.children, id) : [] }));
}

function addToTree(list: CommentResponse[], item: CommentResponse, parentId: number | null): CommentResponse[] {
  if (!parentId) return [...list, item];
  return list.map((c) => (c.id === parentId ? { ...c, children: [...(c.children || []), item] } : c.children?.length ? { ...c, children: addToTree(c.children, item, parentId) } : c));
}

function countAll(list: CommentResponse[]): number {
  return list.reduce((n, c) => n + 1 + countAll(c.children || []), 0);
}

function findInTree(list: CommentResponse[], id: number): CommentResponse | null {
  for (const c of list) {
    if (c.id === id) return c;
    const f = findInTree(c.children || [], id);
    if (f) return f;
  }
  return null;
}

// ============================================================================
// MAIN
// ============================================================================

export function ProductComments({ productSlug, ownerId }: { productSlug: string; ownerId?: number }) {
  const { user, isAuthenticated, login } = useAuth();
  const [comments, setComments] = useState<CommentResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState<SortOption>("top");
  const [replyTo, setReplyTo] = useState<number | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());

  useEffect(() => {
    (async () => {
      setLoading(true);
      const res = await getProductComments(productSlug);
      if (res.success && res.data) setComments(res.data.comments);
      setLoading(false);
    })();
  }, [productSlug]);

  const sorted = useMemo(() => {
    const doSort = (l: CommentResponse[]): CommentResponse[] => {
      const s = [...l].sort((a, b) => (sort === "top" ? b.voteCount - a.voteCount : sort === "new" ? +new Date(b.createdAt) - +new Date(a.createdAt) : +new Date(a.createdAt) - +new Date(b.createdAt)));
      return s.map((c) => ({ ...c, children: c.children ? doSort(c.children) : [] }));
    };
    return doSort(comments);
  }, [comments, sort]);

  const total = useMemo(() => countAll(comments), [comments]);

  const vote = useCallback(async (id: number, dir: "up" | "down") => {
    if (!isAuthenticated) { toast.error("Войдите, чтобы голосовать"); return; }
    const c = findInTree(comments, id);
    if (!c) return;
    const curr = c.userVote;
    const newDir: 1 | -1 | 0 = dir === "up" ? (curr === 1 ? 0 : 1) : curr === -1 ? 0 : -1;
    const delta = newDir === 0 ? -curr : curr === 0 ? newDir : newDir - curr;
    const prev = comments;
    setComments((p) => updateInTree(p, id, (x) => ({ ...x, voteCount: x.voteCount + delta, userVote: newDir })));
    try {
      const res = await voteOnComment(id, newDir);
      if (!res.success) throw new Error();
      if (res.data) setComments((p) => updateInTree(p, id, (x) => ({ ...x, voteCount: res.data!.comment.voteCount, userVote: res.data!.comment.userVote })));
    } catch { setComments(prev); toast.error("Ошибка"); }
  }, [comments, isAuthenticated]);

  const post = useCallback(async (text: string, parentId: number | null) => {
    if (!isAuthenticated || !text.trim()) return;
    const temp: CommentResponse = {
      id: -Date.now(), productId: 0, content: text.trim(), parentId, depth: 0, voteCount: 0, userVote: 0, edited: false,
      author: user ? { id: user.id, name: user.name, username: user.username || null, avatarUrl: user.avatarUrl || null } : null,
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), children: [],
    };
    const prev = comments;
    setComments((p) => addToTree(p, temp, parentId));
    setReplyTo(null);
    try {
      const res = await createComment(productSlug, text.trim(), parentId);
      if (!res.success || !res.data) throw new Error();
      setComments((p) => addToTree(removeFromTree(p, temp.id), res.data!, parentId));
    } catch { setComments(prev); toast.error("Ошибка"); }
  }, [comments, isAuthenticated, productSlug, user]);

  const edit = useCallback(async (id: number, text: string) => {
    if (!text.trim()) return;
    const prev = comments;
    setComments((p) => updateInTree(p, id, (c) => ({ ...c, content: text.trim(), edited: true })));
    setEditId(null);
    try { const res = await updateComment(id, text.trim()); if (!res.success) throw new Error(); }
    catch { setComments(prev); toast.error("Ошибка"); }
  }, [comments]);

  const remove = useCallback(async (id: number) => {
    const prev = comments;
    setComments((p) => removeFromTree(p, id));
    try { const res = await deleteComment(id); if (!res.success) throw new Error(); }
    catch { setComments(prev); toast.error("Ошибка"); }
  }, [comments]);

  const report = useCallback(async (id: number) => {
    try { const res = await reportComment(id); if (res.success) toast.success("Жалоба отправлена"); }
    catch { toast.error("Ошибка"); }
  }, []);

  const toggle = useCallback((id: number) => {
    setCollapsed((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, []);

  return (
    <div className="bg-card rounded-none md:rounded-2xl overflow-hidden border-x-0 md:border-x border-y md:border border-border">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border/50 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium">
          <MessageSquare className="w-4 h-4 text-muted-foreground" />
          <span>{total} комментариев</span>
        </div>
        <div className="flex gap-0.5 text-xs">
          {(["top", "new", "oldest"] as const).map((o) => (
            <button key={o} onClick={() => setSort(o)} className={cn("px-2.5 py-1 rounded-full", sort === o ? "bg-muted text-foreground font-medium" : "text-muted-foreground hover:text-foreground")}>
              {o === "top" ? "Лучшие" : o === "new" ? "Новые" : "Старые"}
            </button>
          ))}
        </div>
      </div>

      {/* Input */}
      <div className="p-4 border-b border-border/50">
        {isAuthenticated ? (
          <CommentInput placeholder="Написать комментарий..." onSubmit={(t) => post(t, null)} avatar={user?.avatarUrl} name={user?.name} />
        ) : (
          <button onClick={() => login()} className="w-full py-2.5 text-sm text-muted-foreground hover:text-foreground border border-dashed border-border rounded-xl hover:border-muted-foreground/50 transition-colors">
            Войдите, чтобы комментировать
          </button>
        )}
      </div>

      {/* List */}
      <div className="p-4 overflow-auto max-h-[70vh]">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex gap-3">
                <Skeleton className="w-8 h-8 rounded-full shrink-0" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3 w-28" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-3 w-20" />
                </div>
              </div>
            ))}
          </div>
        ) : sorted.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">Комментариев пока нет</div>
        ) : (
          <ul className="space-y-0">
            {sorted.map((c, i) => (
              <CommentItem
                key={c.id}
                comment={c}
                depth={0}
                isLast={i === sorted.length - 1}
                userId={user?.id}
                ownerId={ownerId}
                isAuth={isAuthenticated}
                replyTo={replyTo}
                editId={editId}
                collapsed={collapsed}
                onVote={vote}
                onReply={setReplyTo}
                onEdit={setEditId}
                onDelete={remove}
                onReport={report}
                onPost={post}
                onSaveEdit={edit}
                onCancel={() => { setReplyTo(null); setEditId(null); }}
                onToggle={toggle}
                onLogin={login}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// INPUT
// ============================================================================

function CommentInput({ placeholder, onSubmit, onCancel, initial = "", avatar, name, autoFocus, mini }: {
  placeholder: string; onSubmit: (t: string) => void; onCancel?: () => void; initial?: string; avatar?: string | null; name?: string; autoFocus?: boolean; mini?: boolean;
}) {
  const [text, setText] = useState(initial);
  const [focused, setFocused] = useState(!!autoFocus);
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    await onSubmit(text);
    setText("");
    setSending(false);
    setFocused(false);
  };

  return (
    <div className={cn("flex gap-3", mini && "gap-2")}>
      {!mini && (
        <Avatar className="w-8 h-8 shrink-0">
          <AvatarImage src={avatar || undefined} />
          <AvatarFallback className="text-xs bg-muted">{name?.[0]?.toUpperCase() || "?"}</AvatarFallback>
        </Avatar>
      )}
      <div className="flex-1">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onFocus={() => setFocused(true)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          rows={focused ? 3 : 1}
          className={cn("w-full px-3 py-2 text-sm bg-muted/50 border-0 rounded-xl resize-none transition-all placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-border", mini && "text-xs py-1.5")}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); } }}
        />
        {focused && (
          <div className="flex justify-end gap-2 mt-2">
            {onCancel && <button onClick={() => { setText(initial); setFocused(false); onCancel(); }} className="px-3 py-1 text-xs text-muted-foreground hover:text-foreground">Отмена</button>}
            <Button size="sm" disabled={!text.trim() || sending} onClick={submit} className="h-7 px-3 text-xs rounded-full">
              {sending && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              Отправить
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// COMMENT ITEM - Pure CSS thread lines with border-radius curves
// ============================================================================

interface CommentItemProps {
  comment: CommentResponse;
  depth: number;
  isLast: boolean;
  userId?: number;
  ownerId?: number;
  isAuth: boolean;
  replyTo: number | null;
  editId: number | null;
  collapsed: Set<number>;
  onVote: (id: number, dir: "up" | "down") => void;
  onReply: (id: number) => void;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
  onReport: (id: number) => void;
  onPost: (text: string, parentId: number) => void;
  onSaveEdit: (id: number, text: string) => void;
  onCancel: () => void;
  onToggle: (id: number) => void;
  onLogin: () => void;
}

function CommentItem({ comment: c, depth, isLast, userId, ownerId, isAuth, replyTo, editId, collapsed, onVote, onReply, onEdit, onDelete, onReport, onPost, onSaveEdit, onCancel, onToggle, onLogin }: CommentItemProps) {
  const isOwn = userId !== undefined && c.author?.id === userId;
  const isProductOwner = ownerId !== undefined && c.author?.id === ownerId;
  const isReply = replyTo === c.id;
  const isEditing = editId === c.id;
  const isClosed = collapsed.has(c.id);
  const hasKids = (c.children?.length ?? 0) > 0;
  const name = c.author?.name || "Аноним";
  const avatar = c.author?.avatarUrl;
  const profileHref = c.author?.username ? `/u/${c.author.username}` : null;

  const countReplies = (x: CommentResponse): number => (x.children?.length ?? 0) + (x.children?.reduce((n, ch) => n + countReplies(ch), 0) ?? 0);
  const replyCount = countReplies(c);

  return (
    <li className="relative list-none">
      {/* For last child: mask parent's line FIRST, then draw L-connector on top */}
      {depth > 0 && isLast && (
        <div
          className="absolute bg-card pointer-events-none z-10"
          style={{
            left: -20,
            top: 5,
            bottom: 0,
            width: 8,
          }}
        />
      )}

      {/* L-shaped curved connector for nested replies - drawn on top of mask */}
      {depth > 0 && (
        <div
          className="absolute border-l-2 border-b-2 border-border rounded-bl-xl pointer-events-none z-20"
          style={{
            left: -19,
            top: 5,
            width: 22,
            height: 18,
          }}
        />
      )}

      {/* Vertical thread line from avatar to children - clickable to collapse */}
      {hasKids && !isClosed && (
        <button
          onClick={() => onToggle(c.id)}
          className="absolute w-5 group transition-all"
          style={{
            left: 4,
            top: 40,
            bottom: 0,
          }}
          aria-label="Свернуть ветку"
        >
          <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-0.5 bg-border rounded-full transition-colors" />
        </button>
      )}


      {/* Comment content */}
      <div className="flex gap-3 py-2">
        {profileHref ? (
          <Link href={profileHref} className="shrink-0 z-30">
            <Avatar className="w-8 h-8 hover:opacity-80 transition-opacity">
              <AvatarImage src={avatar || undefined} />
              <AvatarFallback className="text-xs bg-muted">{name[0]}</AvatarFallback>
            </Avatar>
          </Link>
        ) : (
          <Avatar className="w-8 h-8 shrink-0 z-30">
            <AvatarImage src={avatar || undefined} />
            <AvatarFallback className="text-xs bg-muted">{name[0]}</AvatarFallback>
          </Avatar>
        )}

        <div className="flex-1">
          {/* Header */}
          <div className="mb-0.5">
            <div className="flex items-center gap-1.5 whitespace-nowrap">
              {profileHref ? (
                <Link href={profileHref} className="text-sm font-medium hover:underline">{name}</Link>
              ) : (
                <span className="text-sm font-medium">{name}</span>
              )}
              {isProductOwner && (
                <span className="shrink-0 text-xs px-1.5 py-0.5 rounded-md bg-emerald-600/90 text-white font-medium">
                  Основатель
                </span>
              )}
              {c.author?.linkedProduct && (
                <Link
                  href={`/products/${c.author.linkedProduct.slug}`}
                  className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-border text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                >
                  <img
                    src={c.author.linkedProduct.logoUrl}
                    alt={c.author.linkedProduct.name}
                    className="w-3.5 h-3.5 rounded object-cover shrink-0"
                  />
                  {c.author.linkedProduct.name}
                </Link>
              )}
            </div>
            <div className="flex items-center gap-1 mt-0.5">
              <span className="text-xs text-muted-foreground">{formatDistanceToNow(new Date(c.createdAt), { addSuffix: true, locale: ru })}</span>
              {c.edited && <span className="text-xs text-muted-foreground">· (ред.)</span>}
            </div>
          </div>

          {/* Body */}
          {isEditing ? (
            <CommentInput placeholder="Редактирование..." initial={c.content} onSubmit={(t) => onSaveEdit(c.id, t)} onCancel={onCancel} autoFocus mini />
          ) : isClosed ? (
            <button onClick={() => onToggle(c.id)} className="text-xs text-blue-500 hover:underline mt-1">
              Показать {replyCount} {replyCount === 1 ? "ответ" : replyCount < 5 ? "ответа" : "ответов"}
            </button>
          ) : (
            <>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{c.content}</p>

              {/* Actions */}
              <div className="flex items-center gap-1 mt-2 -ml-1">
                {/* Votes */}
                <div className="flex items-center rounded-full bg-muted/50">
                  <button onClick={() => onVote(c.id, "up")} className={cn("p-1.5 rounded-full hover:bg-muted transition-colors", c.userVote === 1 ? "text-orange-500" : "text-muted-foreground hover:text-orange-500")}>
                    <UpArrow active={c.userVote === 1} />
                  </button>
                  <span className={cn("text-xs font-medium min-w-[20px] text-center", c.userVote === 1 && "text-orange-500", c.userVote === -1 && "text-blue-500")}>
                    {c.voteCount}
                  </span>
                  <button onClick={() => onVote(c.id, "down")} className={cn("p-1.5 rounded-full hover:bg-muted transition-colors", c.userVote === -1 ? "text-blue-500" : "text-muted-foreground hover:text-blue-500")}>
                    <DownArrow active={c.userVote === -1} />
                  </button>
                </div>

                <button onClick={() => isAuth ? onReply(c.id) : onLogin()} className="px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted rounded-full transition-colors">
                  Ответить
                </button>

                {hasKids && (
                  <button onClick={() => onToggle(c.id)} className="px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted rounded-full transition-colors">
                    Скрыть
                  </button>
                )}

                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <button className="p-1.5 text-muted-foreground hover:bg-muted rounded-full transition-colors">
                      <MoreHorizontal className="w-4 h-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-[140px] rounded-xl">
                    {isOwn ? (
                      <>
                        <DropdownMenuItem onClick={() => onEdit(c.id)} className="text-xs gap-2 rounded-lg"><Pencil className="w-3.5 h-3.5" /> Редактировать</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => onDelete(c.id)} className="text-xs gap-2 text-red-500 focus:text-red-500 rounded-lg"><Trash2 className="w-3.5 h-3.5" /> Удалить</DropdownMenuItem>
                      </>
                    ) : (
                      <DropdownMenuItem onClick={() => onReport(c.id)} className="text-xs gap-2 text-red-500 focus:text-red-500 rounded-lg"><Flag className="w-3.5 h-3.5" /> Пожаловаться</DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Reply Input */}
              {isReply && (
                <div className="mt-3">
                  <CommentInput placeholder={`Ответить ${name}...`} onSubmit={(t) => onPost(t, c.id)} onCancel={onCancel} autoFocus mini />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Children */}
      {hasKids && !isClosed && (
        <ul className="ml-8 space-y-0">
          {c.children!.map((child, i) => (
            <CommentItem
              key={child.id}
              comment={child}
              depth={depth + 1}
              isLast={i === c.children!.length - 1}
              userId={userId}
              ownerId={ownerId}
              isAuth={isAuth}
              replyTo={replyTo}
              editId={editId}
              collapsed={collapsed}
              onVote={onVote}
              onReply={onReply}
              onEdit={onEdit}
              onDelete={onDelete}
              onReport={onReport}
              onPost={onPost}
              onSaveEdit={onSaveEdit}
              onCancel={onCancel}
              onToggle={onToggle}
              onLogin={onLogin}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
